/**
 * Projects: a named working directory on a host, its repositories, and what is
 * actually true about them.
 *
 * In the session cockpit a project is exactly that — the place a session runs,
 * the way the operator's `screen` sessions were named after the directory they
 * ran in. Creating a project is naming that place: a project, and at least one
 * repository, which is an absolute directory on one chosen server.
 *
 * The detail screen used to be two panels of what somebody typed into the create
 * form. It said "git: no" about a directory that had been a git checkout for
 * years, because nothing had ever looked. It now reads the host — `POST
 * /projects/{id}/scan` — and every git fact on this page is either evidence from
 * that reading or explicitly marked unread. The three states are kept apart on
 * purpose: read and true, read and false, and never read. A grey "no" for the
 * third is the defect this screen was rebuilt to remove.
 *
 * Removal is a pre-flight, not a confirm box. `GET /projects/{id}/removal-check`
 * says what stops the delete (a live session) and what it costs (repository
 * records forgotten, session history kept), and the dialog states plainly that
 * nothing on the host is touched — because "delete project" reads like it might
 * be.
 */

import { useCallback, useMemo, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import clsx from 'clsx'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type PageResponse } from '@/api/client'
import {
  Page,
  PageHeader,
  Panel,
  Badge,
  Button,
  StatusDot,
  KeyValue,
  EmptyState,
  type Tone,
} from '@/components/primitives'
import {
  QueryState,
  RouteTabs,
  Dialog,
  TextField,
  TextAreaField,
  SelectField,
  Segmented,
  Toolbar,
  Notice,
  CopyButton,
  useApiErrorMessage,
} from '@/components/controls'
import { useConfirm } from '@/components/confirm'
import { FilesTab } from './FilesTab'
import { useT, useLocale } from '@/i18n/context'
import type { Locale } from '@/i18n'
import { formatDateTime, formatRelative } from '@/i18n'
import { projectStateTone, readingTone, riskTone, sessionStateTone } from '@/lib/tone'
import {
  matchesProject,
  needsAttention,
  passesFilter,
  readCheckout,
  readProject,
  type ProjectFilter,
} from './projectsModel'
import type {
  ID,
  ISOTime,
  Meta,
  Project,
  ProjectRemovalCheck,
  ProjectRemovalCheckResponse,
  ProjectState,
  Provider,
  Repository,
  RepositoryScan,
  SessionKind,
  SessionState,
} from '@/types/domain'
import './ProjectsPage.css'

/* ------------------------------------------------------------ Wire shapes */

/** The host fields the project screens need: a name to show and the id to send. */
interface HostLite {
  id: ID
  name: string
  isControlHost: boolean
}

/**
 * api.SessionResponse, narrowed to what this page may honestly render.
 *
 * turnCount, toolCalls* and lastActivityAt are deliberately absent: they are on
 * the wire and no writer ever advances them, so a "12 turns" line here would be
 * a fabricated number. State, host, directory and the two timestamps are real.
 */
interface SessionRow extends Meta {
  kind: SessionKind
  state: SessionState
  provider: Provider
  projectId?: ID
  hostId: ID
  accountProfileId?: ID
  accountLabel?: string
  modelName?: string
  tmuxSession?: string
  workingDir?: string
  effort?: string
  startedAt?: ISOTime
  endedAt?: ISOTime
}

/** One row of the trail, mirroring domain.AuditEvent — the fields a project's
 *  own history needs. The trail records ids only; there is no join on the way
 *  out, so nothing here is dressed up as a name. */
interface AuditRow extends Meta {
  action: string
  actor: string
  actorKind: string
  result: string
  detail?: string
  occurredAt: ISOTime
}

/* ------------------------------------------------------------------- Tabs */

const TABS = ['overview', 'repositories', 'files', 'sessions', 'audit'] as const
type TabId = (typeof TABS)[number]

function isTab(value: string | undefined): value is TabId {
  return value != null && (TABS as readonly string[]).includes(value)
}

/* ----------------------------------------------------------------- Tones */

/** The states a session can be in that mean "this is still going". Matched
 *  against the same set the server calls active, because the sessions endpoint
 *  returns history too. */
const LIVE_SESSION_STATES: readonly SessionState[] = [
  'CREATED',
  'STARTING',
  'RUNNING',
  'INPUT_REQUIRED',
  'IDLE',
  'CHECKPOINTED',
  'HANDING_OFF',
]

function isLive(state: SessionState): boolean {
  return LIVE_SESSION_STATES.includes(state)
}

function resultTone(result: string): Tone {
  switch (result) {
    case 'SUCCESS':
      return 'ok'
    case 'FAILURE':
      return 'err'
    case 'DENIED':
      return 'warn'
    default:
      return 'unknown'
  }
}

/**
 * The tone of a whole reading.
 *
 * `unknown` for never read, `err` for a reading that could not happen or found
 * no directory, `warn` for a directory that is there but whose git state could
 * not be established, `ok` otherwise. A repository that is deliberately not a
 * checkout is not a fault, so it reads neutral rather than amber.
 */
function scanTone(scan: RepositoryScan | undefined): Tone {
  if (!scan) return 'unknown'
  if (scan.error) return 'err'
  if (!scan.pathExists) return 'err'
  if (!scan.gitAvailable) return 'warn'
  return scan.isGit ? 'ok' : 'neutral'
}

/** A commit id is read by its first characters and pasted in full. */
function shortSha(sha: string | undefined): string {
  return sha ? sha.slice(0, 10) : ''
}

/* ------------------------------------------------------------------- Page */

export function ProjectsPage() {
  const { projectId, tab } = useParams<{ projectId: string; tab: string }>()
  if (!projectId) return <ProjectList />
  // The route carries a tab segment; an unrecognised one is redirected rather
  // than rendered blank, and the default is a location an operator can link to.
  if (!isTab(tab)) return <Navigate to={`/projects/${projectId}/overview`} replace />
  return <ProjectDetailView projectId={projectId} tab={tab} />
}

/* ------------------------------------------------------------------- List */

/**
 * The project list.
 *
 * Every card used to carry a name, the slug, a repository count and a relative
 * scan time — and since the slug is the name lower-cased and every project on
 * this deployment holds exactly one repository, eleven cards said eleven names
 * and nothing else. All the while `GET /projects` was returning the node's full
 * scan of each checkout and none of it was drawn.
 *
 * So the card now answers the questions an operator opens this screen with:
 * where does this project live, what state is its checkout in, what happened in
 * it last, can an agent be told what to do here, and is anything running right
 * now. The search and the filters exist because eleven projects is already too
 * many to scan by eye and this list only grows.
 */
function ProjectList() {
  const t = useT()
  const { locale } = useLocale()
  const [creating, setCreating] = useState(false)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<ProjectFilter>('all')

  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<PageResponse<Project>>('/projects'),
  })
  const hosts = useHosts()
  // The one fact the project record cannot carry: whether anything is running
  // in it at this moment. Sessions are keyed by project, so one request answers
  // it for every card at once.
  const sessions = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get<PageResponse<SessionRow>>('/sessions'),
    refetchInterval: 15_000,
  })

  const hostName = useCallback(
    (id: string) => hosts.data?.items.find((h) => h.id === id)?.name,
    [hosts.data],
  )

  const items = useMemo(() => projects.data?.items ?? [], [projects.data])

  const liveByProject = useMemo(() => {
    const out = new Map<string, number>()
    for (const s of sessions.data?.items ?? []) {
      if (!s.projectId || !isLive(s.state)) continue
      out.set(s.projectId, (out.get(s.projectId) ?? 0) + 1)
    }
    return out
  }, [sessions.data])

  const shown = useMemo(
    () =>
      items.filter(
        (p) =>
          matchesProject(p, query, hostName) &&
          passesFilter(filter, readProject(p), liveByProject.get(p.id) ?? 0),
      ),
    [items, query, filter, hostName, liveByProject],
  )

  return (
    <Page
      header={
        <PageHeader
          title={t('project.title')}
          subtitle={
            shown.length === items.length
              ? t('common.count', { count: items.length })
              : t('project.matchCount', { shown: shown.length, total: items.length })
          }
          actions={
            <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
              {t('project.add')}
            </Button>
          }
        >
          {/* The filters go in `actions` so they sit at the right edge: the
              toolbar's filter area is an equal-column grid, and a search box
              and a four-way switch sharing it made both half a row wide. */}
          <Toolbar
            actions={
              <Segmented
                size="sm"
                value={filter}
                onChange={setFilter}
                ariaLabel={t('common.filters')}
                options={[
                  { value: 'all', label: t('project.filter.all') },
                  { value: 'attention', label: t('project.filter.attention') },
                  { value: 'running', label: t('project.filter.running') },
                  { value: 'unconfigured', label: t('project.filter.unconfigured') },
                ]}
              />
            }
          >
            <TextField
              label={t('topbar.search')}
              type="search"
              value={query}
              onChange={setQuery}
              placeholder={t('project.search')}
            />
          </Toolbar>
        </PageHeader>
      }
    >
      {creating && <ProjectDialog onClose={() => setCreating(false)} />}

      <QueryState
        pending={projects.isPending}
        error={projects.error}
        onRetry={() => void projects.refetch()}
        empty={shown.length === 0}
        emptyTitle={items.length === 0 ? t('project.empty') : t('project.noMatches')}
        emptyHint={items.length === 0 ? t('project.emptyHint') : t('project.noMatchesHint')}
        emptyAction={
          items.length === 0 ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              {t('project.add')}
            </Button>
          ) : undefined
        }
      >
        <div className="k-cardgrid">
          {shown.map((p) => (
            <ProjectCard
              key={p.id}
              project={p}
              live={liveByProject.get(p.id) ?? 0}
              hostName={hostName}
              locale={locale}
            />
          ))}
        </div>
      </QueryState>
    </Page>
  )
}

/**
 * One project, read the way an operator reads it.
 *
 * The order is deliberate and is the order the questions are asked in: is
 * anything happening here, where is it, what state is the checkout in, what
 * happened last, and could an agent be given work here at all. Everything on it
 * comes from the scan the node already performed — nothing is inferred, and an
 * unread reading says "unread" rather than borrowing the look of a good one.
 */
function ProjectCard({
  project,
  live,
  hostName,
  locale,
}: {
  project: Project
  live: number
  hostName: (id: string) => string | undefined
  locale: Locale
}) {
  const t = useT()
  const r = readProject(project)
  const attention = needsAttention(r)

  const where =
    r.hosts.length === 1
      ? (hostName(r.hosts[0]!) ?? shortSha(r.hosts[0]!))
      : r.hosts.length > 1
        ? t('project.hostsN', { count: r.hosts.length })
        : null
  const branch =
    r.branches.length === 1
      ? r.branches[0]
      : r.branches.length > 1
        ? t('project.branchesN', { count: r.branches.length })
        : null

  return (
    <Panel
      framed
      className={clsx('k-projcard', attention && 'k-projcard--attention')}
      title={
        <div className="k-projcard__head">
          <StatusDot
            tone={live > 0 ? 'accent' : projectStateTone(project.state)}
            live={live > 0}
            label={live > 0 ? t('project.liveSessions', { count: live }) : project.state}
          />
          <Link to={`/projects/${project.id}/overview`} className="k-projcard__name">
            {project.name}
          </Link>
          {live > 0 && (
            <Badge tone="accent" subtle mono>
              {live}
            </Badge>
          )}
          <span className="k-projcard__spacer" />
          {/* The risk level in words. `L2_VALIDATED_CHANGE` is the wire value
              and belongs in the identity panel; on a card it is jargon where a
              sentence fits. */}
          <Badge tone={riskTone(project.riskLevel)} subtle title={project.riskLevel}>
            {t(`risk.${project.riskLevel}` as never)}
          </Badge>
        </div>
      }
    >
      <div className="k-projcard__body">
        {project.description && <p className="k-projcard__desc">{project.description}</p>}

        {r.repositories === 0 ? (
          <p className="k-projcard__empty">
            <Badge tone="unknown" subtle>
              {t('project.noRepositories')}
            </Badge>{' '}
            <span className="k-sub">{t('project.noRepositoriesHint')}</span>
          </p>
        ) : (
          <>
            {/* Where the work lives, on one line: host, branch, and whether the
                tree is clean. Unknown keeps its own colour — a tree nothing has
                read is not a clean one. */}
            <p className="k-projcard__where">
              {where && <span className="k-projcard__host">{where}</span>}
              {branch && (
                <>
                  <span className="k-projcard__sep">·</span>
                  <span className="k-mono k-projcard__branch">{branch}</span>
                </>
              )}
              <span className="k-projcard__sep">·</span>
              {r.dirty > 0 ? (
                <span className="k-projcard__tree k-projcard__tree--dirty">
                  {t('project.treeDirty')}
                </span>
              ) : r.dirtyUnknown > 0 ? (
                <span className="k-projcard__tree k-projcard__tree--unknown">
                  {t('project.treeUnknown')}
                </span>
              ) : (
                <span className="k-projcard__tree k-projcard__tree--clean">
                  {t('project.treeClean')}
                </span>
              )}
            </p>

            {r.head?.subject && (
              <p className="k-projcard__commit" title={r.head.subject}>
                {r.head.subject}
              </p>
            )}
            {r.head?.at && (
              <p className="k-projcard__commitmeta">
                <span className="k-mono">{shortSha(r.head.sha)}</span>
                <span className="k-projcard__sep">·</span>
                <span>{formatRelative(r.head.at, locale)}</span>
                {r.head.author && (
                  <>
                    <span className="k-projcard__sep">·</span>
                    <span className="k-projcard__author">{r.head.author}</span>
                  </>
                )}
              </p>
            )}

            {(r.unread > 0 || r.broken > 0 || r.credentialOverrides.length > 0) && (
              <p className="k-projcard__flags">
                {r.broken > 0 && (
                  <Badge tone="err" subtle>
                    {t('project.brokenN', { count: r.broken })}
                  </Badge>
                )}
                {r.unread > 0 && (
                  <Badge tone="unknown" subtle>
                    {t('project.unreadN', { count: r.unread })}
                  </Badge>
                )}
                {/* On the card because the attention filter lands here: a row
                    an operator filtered to and then cannot see a reason on is
                    a row that sends them hunting. */}
                {r.credentialOverrides.length > 0 && (
                  <Badge tone="warn" subtle title={t('project.credentialOverrideHint')}>
                    {t('project.credentialOverrideN', { count: r.credentialOverrides.length })}
                  </Badge>
                )}
              </p>
            )}

            <p className="k-projcard__chips">
              {r.stack.map((s) => (
                <Badge key={s} tone="neutral" subtle mono>
                  {s}
                </Badge>
              ))}
              {/* The agent configuration is the one chip that changes what a
                  session started here will do, so it is stated even when it is
                  absent — an empty answer is the answer that matters. */}
              {r.agents.length > 0 ? (
                r.agents.map((a) => (
                  <Badge key={a} tone="info" subtle>
                    {t(`project.agent${a[0]!.toUpperCase()}${a.slice(1)}` as never)}
                  </Badge>
                ))
              ) : (
                <Badge tone="warn" subtle title={t('project.agentNoneHint')}>
                  {t('project.agentNone')}
                </Badge>
              )}
            </p>
          </>
        )}

        <p className="k-projcard__foot">
          <span className="k-label">{t('project.lastScan')}</span>{' '}
          {project.lastScanAt ? (
            <span>{formatRelative(project.lastScanAt, locale)}</span>
          ) : (
            <Badge tone="unknown" subtle>
              {t('project.scanUnread')}
            </Badge>
          )}
        </p>
      </div>
    </Panel>
  )
}

/* ------------------------------------------------------------- Create dialog */

interface CreatedProject {
  id: ID
}

/**
 * ProjectDialog names a place: a project, plus its first repository — an
 * absolute directory on one chosen server. The two are one gesture here because
 * a project with no repository can run nothing, so the create flow always ends
 * with somewhere to run. Further repositories are added from the project page.
 */
function ProjectDialog({ onClose }: { onClose: () => void }) {
  const t = useT()
  const queryClient = useQueryClient()
  const apiError = useApiErrorMessage()
  const hosts = useHosts()
  const hostItems = hosts.data?.items ?? []

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [hostId, setHostId] = useState('')
  const [path, setPath] = useState('')
  const [repoName, setRepoName] = useState('')

  const create = useMutation({
    mutationFn: async () => {
      const project = await api.post<CreatedProject>('/projects', {
        name: name.trim(),
        description: description.trim() || undefined,
      })
      // The repository is a second call because it is a distinct resource
      // (POST /repositories, embedded into the project). If it fails the project
      // still exists and its page offers "add repository", so no work is lost.
      await api.post('/repositories', {
        projectId: project.id,
        name: repoName.trim() || name.trim(),
        hostId,
        path: path.trim(),
      })
      return project
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      onClose()
    },
  })

  const pathValid = path.trim().startsWith('/')
  const canSubmit = name.trim() !== '' && hostId !== '' && pathValid && !create.isPending

  return (
    <Dialog
      title={t('project.newTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => create.mutate()}
            disabled={!canSubmit}
            busy={create.isPending}
          >
            {t('project.create')}
          </Button>
        </>
      }
    >
      <div className="k-form">
        <TextField
          label={t('project.name')}
          value={name}
          onChange={setName}
          placeholder={t('project.namePlaceholder')}
        />
        <TextField
          label={t('project.description')}
          value={description}
          onChange={setDescription}
          placeholder={t('project.descriptionHint')}
        />

        <RepositoryFields
          hostItems={hostItems}
          hostsPending={hosts.isPending}
          hostId={hostId}
          setHostId={setHostId}
          path={path}
          setPath={setPath}
          repoName={repoName}
          setRepoName={setRepoName}
        />

        {create.error && <Notice tone="err">{apiError(create.error).title}</Notice>}
      </div>
    </Dialog>
  )
}

function useHosts() {
  return useQuery({
    queryKey: ['hosts'],
    queryFn: () => api.get<PageResponse<HostLite>>('/hosts'),
  })
}

/* -------------------------------------------------------- Add-repository dialog */

function AddRepositoryDialog({
  projectId,
  onClose,
}: {
  projectId: string
  onClose: () => void
}) {
  const t = useT()
  const queryClient = useQueryClient()
  const apiError = useApiErrorMessage()
  const hosts = useHosts()
  const hostItems = hosts.data?.items ?? []

  const [hostId, setHostId] = useState('')
  const [path, setPath] = useState('')
  const [repoName, setRepoName] = useState('')

  const add = useMutation({
    mutationFn: () =>
      api.post('/repositories', {
        projectId,
        name:
          repoName.trim() ||
          path.trim().split('/').filter(Boolean).pop() ||
          t('project.repositories'),
        hostId,
        path: path.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      onClose()
    },
  })

  const canSubmit = hostId !== '' && path.trim().startsWith('/') && !add.isPending

  return (
    <Dialog
      title={t('project.addRepositoryTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => add.mutate()}
            disabled={!canSubmit}
            busy={add.isPending}
          >
            {t('project.addRepository')}
          </Button>
        </>
      }
    >
      <div className="k-form">
        <RepositoryFields
          hostItems={hostItems}
          hostsPending={hosts.isPending}
          hostId={hostId}
          setHostId={setHostId}
          path={path}
          setPath={setPath}
          repoName={repoName}
          setRepoName={setRepoName}
        />
        {add.error && <Notice tone="err">{apiError(add.error).title}</Notice>}
      </div>
    </Dialog>
  )
}

/** The shared repository inputs: which server, which absolute directory, and an
 *  optional name. Both the create dialog and "add repository" collect the same
 *  three, so they live in one place. */
function RepositoryFields({
  hostItems,
  hostsPending,
  hostId,
  setHostId,
  path,
  setPath,
  repoName,
  setRepoName,
}: {
  hostItems: HostLite[]
  hostsPending: boolean
  hostId: string
  setHostId: (v: string) => void
  path: string
  setPath: (v: string) => void
  repoName: string
  setRepoName: (v: string) => void
}) {
  const t = useT()

  if (!hostsPending && hostItems.length === 0) {
    return <Notice tone="warn">{t('project.noHosts')}</Notice>
  }

  return (
    <>
      <SelectField
        label={t('account.host')}
        value={hostId}
        onChange={setHostId}
        placeholder={t('project.selectHost')}
        options={hostItems.map((h) => ({
          value: h.id,
          label: h.isControlHost ? `${h.name} · ${t('project.controlHost')}` : h.name,
        }))}
      />
      <TextField
        label={t('project.path')}
        value={path}
        onChange={setPath}
        placeholder={t('project.pathPlaceholder')}
        hint={t('project.pathHint')}
        mono
      />
      <TextField
        label={t('project.repoName')}
        value={repoName}
        onChange={setRepoName}
        placeholder={t('project.repoNameHint')}
      />
    </>
  )
}

/* ----------------------------------------------------------------- Detail */

function ProjectDetailView({ projectId, tab }: { projectId: string; tab: TabId }) {
  const t = useT()
  const queryClient = useQueryClient()
  const apiError = useApiErrorMessage()
  const [addingRepo, setAddingRepo] = useState(false)
  const [editing, setEditing] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
  })
  // The sessions feed is this project's whole history, not its live set, which is
  // why the counts below filter by state rather than trusting the endpoint.
  const sessions = useQuery({
    queryKey: ['project-sessions', projectId],
    queryFn: () =>
      api.get<PageResponse<SessionRow>>('/sessions', { projectId, limit: 200 }),
  })

  const p = project.data
  const repos = p?.repositories ?? []
  const sessionRows = sessions.data?.items ?? []
  const liveCount = sessionRows.filter((s) => isLive(s.state)).length

  /**
   * One scan mutation for both triggers. `repositoryId` scopes it to one
   * repository; without it every repository is read in one request, which is one
   * document write rather than several racing ones.
   */
  const scan = useMutation({
    mutationFn: (repositoryId?: ID) =>
      api.post<Project>(`/projects/${projectId}/scan`, {
        repositoryId,
        version: p?.version,
      }),
    onSuccess: (updated) => {
      // The response *is* the project, so the cache is seeded from it rather than
      // refetched: the reading just happened and a second GET could only show
      // something older.
      queryClient.setQueryData(['project', projectId], updated)
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })

  const tabItems = [
    { to: `/projects/${projectId}/overview`, label: t('project.tab.overview') },
    {
      to: `/projects/${projectId}/repositories`,
      label: t('project.tab.repositories'),
      count: repos.length,
    },
    { to: `/projects/${projectId}/files`, label: t('files.tab') },
    {
      to: `/projects/${projectId}/sessions`,
      label: t('project.tab.sessions'),
      count: sessionRows.length,
    },
    { to: `/projects/${projectId}/audit`, label: t('project.tab.audit') },
  ]

  return (
    <Page
      header={
        <PageHeader
          title={p?.name ?? t('project.title')}
          subtitle={p?.slug}
          actions={
            <div className="k-proj__actions">
              <Button
                size="sm"
                onClick={() => scan.mutate(undefined)}
                busy={scan.isPending}
                disabled={!p || repos.length === 0}
                title={repos.length === 0 ? t('project.scanNoRepositories') : undefined}
              >
                {t('project.scan')}
              </Button>
              <Button size="sm" variant="default" onClick={() => setEditing(true)} disabled={!p}>
                {t('action.edit')}
              </Button>
              <Button size="sm" variant="danger" onClick={() => setDeleting(true)} disabled={!p}>
                {t('action.delete')}
              </Button>
              <Link to="/projects" className="k-proj__back">
                ← {t('project.backToList')}
              </Link>
            </div>
          }
        >
          <RouteTabs items={tabItems} ariaLabel={t('project.title')} />
        </PageHeader>
      }
    >
      {addingRepo && p && (
        <AddRepositoryDialog projectId={projectId} onClose={() => setAddingRepo(false)} />
      )}
      {editing && p && <EditProjectDialog project={p} onClose={() => setEditing(false)} />}
      {deleting && p && <DeleteProjectDialog project={p} onClose={() => setDeleting(false)} />}

      <QueryState
        pending={project.isPending}
        error={project.error}
        onRetry={() => void project.refetch()}
      >
        {p && (
          <div className="k-projtab">
            <IdentityStrip project={p} liveSessions={liveCount} />

            {scan.error && <Notice tone="err">{apiError(scan.error).title}</Notice>}

            {tab === 'overview' && (
              <OverviewTab project={p} sessions={sessionRows} />
            )}
            {tab === 'repositories' && (
              <RepositoriesTab
                project={p}
                onAdd={() => setAddingRepo(true)}
                onScan={(id) => scan.mutate(id)}
                scanningId={scan.isPending ? (scan.variables ?? 'all') : undefined}
              />
            )}
            {tab === 'files' && <FilesTab project={p} />}
            {tab === 'sessions' && (
              <SessionsTab
                rows={sessionRows}
                pending={sessions.isPending}
                error={sessions.error}
                onRetry={() => void sessions.refetch()}
              />
            )}
            {tab === 'audit' && <AuditTab projectId={projectId} />}
          </div>
        )}
      </QueryState>
    </Page>
  )
}

/* ---------------------------------------------------------- Identity strip */

/**
 * The strip under the tabs: the four facts an operator checks before doing
 * anything else, on one line, in fixed positions.
 *
 * It is a strip rather than a card grid because it is read across, not down —
 * state, then where the work lives, then whether what Kimene knows about this
 * project was ever verified, then how fresh the record is.
 */
function IdentityStrip({
  project,
  liveSessions,
}: {
  project: Project
  liveSessions: number
}) {
  const t = useT()
  const { locale } = useLocale()
  const repos = project.repositories ?? []
  const gitCount = repos.filter((r) => r.isGit).length
  const hostCount = new Set(repos.map((r) => r.hostId)).size
  const unread = repos.filter((r) => !r.scan).length

  return (
    <Panel flush className="k-proj__strippanel">
      <div className="k-proj__strip">
        <div className="k-proj__cell">
          <span className="k-label">{t('project.state')}</span>
          <span className="k-inline">
            <StatusDot
              tone={projectStateTone(project.state)}
              live={project.state === 'ACTIVE'}
              label={project.state}
            />
            <Badge tone={projectStateTone(project.state)} subtle>
              {project.state}
            </Badge>
            <Badge tone={riskTone(project.riskLevel)} subtle mono>
              {project.riskLevel}
            </Badge>
          </span>
        </div>

        <div className="k-proj__cell">
          <span className="k-label">{t('project.repositories')}</span>
          <span className="k-proj__figures">
            <Figure value={repos.length} label={t('project.repositories')} />
            <Figure value={gitCount} label={t('project.git')} />
            <Figure value={hostCount} label={t('project.hosts')} />
          </span>
        </div>

        <div className="k-proj__cell">
          <span className="k-label">{t('project.lastScan')}</span>
          {project.lastScanAt ? (
            <span className="k-inline">
              <span className="k-mono">{formatRelative(project.lastScanAt, locale)}</span>
              {unread > 0 && (
                <Badge tone="unknown" subtle mono>
                  {unread} {t('project.scanUnread')}
                </Badge>
              )}
            </span>
          ) : (
            <Badge tone="unknown" subtle>
              {t('project.scanUnread')}
            </Badge>
          )}
        </div>

        <div className="k-proj__cell">
          <span className="k-label">{t('project.fingerprint')}</span>
          {project.configFingerprint ? (
            <span className="k-inline">
              <span className="k-mono k-projconf__hash" title={project.configFingerprint}>
                {project.configFingerprint}
              </span>
              <CopyButton value={project.configFingerprint} ariaLabel={t('common.copy')} />
            </span>
          ) : (
            <Badge tone="unknown" subtle>
              {t('project.scanUnread')}
            </Badge>
          )}
        </div>

        <div className="k-proj__cell k-proj__cell--end">
          <span className="k-label">{t('project.openSessions')}</span>
          <span className="k-inline">
            <StatusDot
              tone={liveSessions > 0 ? 'accent' : 'neutral'}
              live={liveSessions > 0}
              label={t('project.openSessions')}
            />
            <span className="k-proj__figure-value k-mono">{liveSessions}</span>
          </span>
        </div>
      </div>
    </Panel>
  )
}

function Figure({ value, label }: { value: number; label: string }) {
  return (
    <span className="k-proj__figure" title={label}>
      <span className="k-proj__figure-value k-mono">{value}</span>
      <span className="k-proj__figure-unit">{label}</span>
    </span>
  )
}

/* --------------------------------------------------------------- Overview */

function OverviewTab({
  project,
  sessions,
}: {
  project: Project
  sessions: SessionRow[]
}) {
  const t = useT()
  const { locale } = useLocale()
  const hosts = useHosts()
  const hostName = (id: string) => hosts.data?.items.find((h) => h.id === id)?.name
  const repos = project.repositories ?? []
  const reading = readProject(project)
  return (
    <div className="k-projov">
      <div className="k-projov__col">
        {/* Where the work actually runs.
            This panel used to be four figures — repositories, git repositories,
            open sessions, sessions — every one of which the strip a hundred
            pixels above already carried. Counting the same things twice is not
            an overview; what an operator needs first is *which* machine holds
            the checkout, on what branch, in what state. */}
        <Panel title={t('project.whereItRuns')}>
          {repos.length === 0 ? (
            <EmptyState
              title={t('project.noRepositories')}
              hint={t('project.noRepositoriesHint')}
            />
          ) : (
            <ul className="k-projwhere">
              {repos.map((repo) => {
                const c = readCheckout(repo)
                const scan = repo.scan
                return (
                  <li className="k-projwhere__row" key={repo.id}>
                    <div className="k-projwhere__head">
                      <StatusDot tone={scanTone(scan)} label={repo.name} />
                      <span className="k-projwhere__name">{repo.name}</span>
                      <span className="k-projwhere__host">
                        {hostName(repo.hostId) ?? shortSha(repo.hostId)}
                      </span>
                      {c.unread ? (
                        <Badge tone="unknown" subtle>
                          {t('project.scanUnread')}
                        </Badge>
                      ) : c.broken ? (
                        <Badge tone="err" subtle>
                          {t('project.scanFailed')}
                        </Badge>
                      ) : null}
                    </div>

                    <p className="k-mono k-projwhere__path" title={repo.path}>
                      {repo.path}
                    </p>

                    {!c.unread && !c.broken && (
                      <p className="k-projwhere__git">
                        {c.branch && <span className="k-mono k-projcard__branch">{c.branch}</span>}
                        <span className="k-projcard__sep">·</span>
                        {c.dirty ? (
                          <span className="k-projcard__tree--dirty">
                            {scan?.dirtyFileCount != null
                              ? t('project.dirtyCount', { count: scan.dirtyFileCount })
                              : t('project.treeDirty')}
                          </span>
                        ) : c.dirtyUnknown ? (
                          <span className="k-projcard__tree--unknown">
                            {t('project.treeUnknown')}
                          </span>
                        ) : (
                          <span className="k-projcard__tree--clean">{t('project.treeClean')}</span>
                        )}
                        {scan?.headCommit && (
                          <>
                            <span className="k-projcard__sep">·</span>
                            <span className="k-mono">{shortSha(scan.headCommit)}</span>
                          </>
                        )}
                        {scan?.headAt && (
                          <>
                            <span className="k-projcard__sep">·</span>
                            <span>{formatRelative(scan.headAt, locale)}</span>
                          </>
                        )}
                      </p>
                    )}

                    {scan?.headSubject && (
                      <p className="k-projwhere__subject" title={scan.headSubject}>
                        {scan.headSubject}
                      </p>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
          {project.description && <p className="k-proj__desc">{project.description}</p>}
        </Panel>

        {/* Whether an agent can be told what to do here at all.
            This is the one project fact that changes how a session behaves the
            moment it starts, and it was readable nowhere: the scan reports the
            inventory, and the overview showed a repository count. */}
        <Panel title={t('project.agentInstructions')}>
          {reading.agents.length > 0 ? (
            <>
              <p className="k-chips">
                {reading.agents.map((a) => (
                  <Badge key={a} tone="info" subtle>
                    {t(`project.agent${a[0]!.toUpperCase()}${a.slice(1)}` as never)}
                  </Badge>
                ))}
              </p>
              <p className="k-proj__note">{t('project.agentPresentHint')}</p>
            </>
          ) : reading.repositories === 0 ? (
            <p className="k-proj__note">{t('project.noRepositoriesHint')}</p>
          ) : (
            <Notice tone="warn">{t('project.agentNoneHint')}</Notice>
          )}
        </Panel>

        <Panel title={t('project.policy')}>
          <KeyValue
            items={[
              {
                key: 'mode',
                label: t('project.routingMode'),
                value: project.routing?.mode ? (
                  <Badge tone="info" subtle mono>
                    {project.routing.mode}
                  </Badge>
                ) : (
                  <span className="k-dash">—</span>
                ),
              },
              {
                key: 'provider',
                label: t('project.allowProviderSwitch'),
                value: <YesNo value={project.routing?.allowProviderSwitch} />,
              },
              {
                key: 'model',
                label: t('project.allowModelSwitch'),
                value: <YesNo value={project.routing?.allowModelSwitch} />,
              },
              {
                key: 'boundary',
                label: t('project.safeCheckpointOnly'),
                value: <YesNo value={project.routing?.requireSafeCheckpointForSwitch} />,
              },
              {
                key: 'reviewer',
                label: t('project.qualityReviewer'),
                value: <YesNo value={project.quality?.requireIndependentReviewer} />,
              },
              {
                key: 'cross',
                label: t('project.qualityCrossProvider'),
                value: <YesNo value={project.quality?.requireDifferentProviderForReview} />,
              },
              {
                key: 'gates',
                label: t('project.qualityGates'),
                value:
                  project.quality?.requiredGates && project.quality.requiredGates.length > 0 ? (
                    <span className="k-chips">
                      {project.quality.requiredGates.map((g) => (
                        <Badge key={g} tone="neutral" subtle mono>
                          {g}
                        </Badge>
                      ))}
                    </span>
                  ) : (
                    <span className="k-dash">—</span>
                  ),
              },
            ]}
          />
          <p className="k-proj__note">{t('project.routingNote')}</p>
        </Panel>
      </div>

      <div className="k-projov__col">
        <Panel title={t('project.identity')}>
          <KeyValue
            items={[
              { key: 'slug', label: t('project.slug'), value: project.slug, mono: true },
              {
                key: 'state',
                label: t('project.state'),
                value: (
                  <Badge tone={projectStateTone(project.state)} subtle>
                    {project.state}
                  </Badge>
                ),
              },
              {
                key: 'risk',
                label: t('project.riskLevel'),
                value: (
                  <Badge tone={riskTone(project.riskLevel)} subtle mono>
                    {project.riskLevel}
                  </Badge>
                ),
              },
              {
                key: 'tz',
                label: t('project.timezone'),
                value: project.timezone || <span className="k-dash">—</span>,
                mono: true,
              },
              {
                key: 'created',
                label: t('project.created'),
                value: `${formatDateTime(project.createdAt, locale)} · ${project.createdBy}`,
                mono: true,
              },
              {
                key: 'updated',
                label: t('project.updated'),
                value: `${formatDateTime(project.updatedAt, locale)} · ${project.updatedBy}`,
                mono: true,
              },
              {
                key: 'version',
                label: t('project.recordVersion'),
                value: String(project.version),
                mono: true,
              },
              { key: 'id', label: t('common.id'), value: project.id, mono: true },
            ]}
          />
        </Panel>

        {/* The five most recent sessions, from the feed the tab already loaded.
            This is a session cockpit: "what has actually run here" belongs on the
            first screen, not one tab away. */}
        <Panel title={t('project.recentSessions')}>
          {sessions.length === 0 ? (
            <EmptyState title={t('project.noSessionsEver')} />
          ) : (
            <ul className="k-projov__sessions">
              {sessions.slice(0, 5).map((s) => (
                <li key={s.id}>
                  <StatusDot
                    tone={sessionStateTone(s.state)}
                    live={isLive(s.state)}
                    label={s.state}
                  />
                  <Link to={`/live/${s.id}`} className="k-mono k-projov__sessionlink">
                    {s.tmuxSession ?? s.provider}
                  </Link>
                  <span className="k-projov__sessionmeta">
                    {s.accountLabel ?? s.provider}
                  </span>
                  <span className="k-mono k-projrepo__when">
                    {formatRelative(s.startedAt ?? s.createdAt, locale)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  )
}

function YesNo({ value }: { value: boolean | undefined }) {
  const t = useT()
  if (value == null) {
    return (
      <Badge tone="unknown" subtle>
        {t('state.unknown')}
      </Badge>
    )
  }
  return (
    <Badge tone={value ? 'ok' : 'neutral'} subtle>
      {value ? t('common.yes') : t('common.no')}
    </Badge>
  )
}

/* ----------------------------------------------------------- Repositories */

function RepositoriesTab({
  project,
  onAdd,
  onScan,
  scanningId,
}: {
  project: Project
  onAdd: () => void
  onScan: (repositoryId?: ID) => void
  scanningId: ID | 'all' | undefined
}) {
  const t = useT()
  const confirm = useConfirm()
  const queryClient = useQueryClient()
  const hosts = useHosts()
  const repos = project.repositories ?? []

  const hostName = (id: ID) => hosts.data?.items.find((h) => h.id === id)?.name ?? id

  const del = useMutation({
    mutationFn: (repo: Repository) =>
      api.del(`/repositories/${repo.id}?projectId=${project.id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['project', project.id] })
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })

  const removeRepo = async (repo: Repository) => {
    const ok = await confirm({
      title: t('project.removeRepository'),
      message: t('project.removeRepositoryConfirm', { name: repo.name }),
      confirmLabel: t('action.delete'),
      tone: 'danger',
    })
    if (ok) del.mutate(repo)
  }

  return (
    <Panel
      title={t('project.repositories')}
      actions={
        <Button size="sm" variant="default" onClick={onAdd}>
          {t('project.addRepository')}
        </Button>
      }
    >
      {repos.length === 0 ? (
        <EmptyState title={t('project.noRepositories')} hint={t('project.noRepositoriesHint')} />
      ) : (
        <div className="k-projrepos">
          {repos.map((r) => (
            <RepositoryCard
              key={r.id}
              repo={r}
              hostName={hostName(r.hostId)}
              onScan={() => onScan(r.id)}
              scanning={scanningId === r.id || scanningId === 'all'}
              onRemove={() => void removeRepo(r)}
              removing={del.isPending && del.variables?.id === r.id}
            />
          ))}
        </div>
      )}
    </Panel>
  )
}

/**
 * One repository, as an instrument.
 *
 * The four cells across the top are always in the same positions — directory,
 * git, branch, changes — so reading down a column of repositories answers one
 * question at a time. A cell that was never read is hatched by its `unknown`
 * tone rather than showing a plausible zero.
 */
function RepositoryCard({
  repo,
  hostName,
  onScan,
  scanning,
  onRemove,
  removing,
}: {
  repo: Repository
  hostName: string
  onScan: () => void
  scanning: boolean
  onRemove: () => void
  removing: boolean
}) {
  const t = useT()
  const { locale } = useLocale()
  const scan = repo.scan
  const read = scan != null && !scan.error

  return (
    <Panel
      framed
      className="k-projrepo"
      title={
        <div className="k-projrepo__head">
          <StatusDot tone={scanTone(scan)} label={repo.name} />
          <span className="k-projrepo__name">{repo.name}</span>
          <Badge tone="neutral" subtle mono>
            {hostName}
          </Badge>
        </div>
      }
      actions={
        <>
          <Button size="sm" variant="default" onClick={onScan} busy={scanning}>
            {t('project.scanOne')}
          </Button>
          <Button size="sm" variant="ghost" onClick={onRemove} busy={removing}>
            {t('action.delete')}
          </Button>
        </>
      }
    >
      <div className="k-projrepo__body">
        {/* The fixed reading row. */}
        <div className="k-projrepo__vitals">
          <ReadingCell
            label={t('project.path')}
            tone={scan ? (scan.error ? 'unknown' : readingTone(scan.pathExists)) : 'unknown'}
            value={
              scan?.error
                ? t('project.scanUnread')
                : scan
                  ? scan.pathExists
                    ? t('project.configPresent')
                    : t('project.configMissing')
                  : t('project.scanUnread')
            }
          />
          <ReadingCell
            label={t('project.git')}
            tone={scan && read ? (scan.gitAvailable ? readingTone(scan.isGit) : 'warn') : 'unknown'}
            value={
              !read
                ? t('project.scanUnread')
                : !scan?.gitAvailable
                  ? t('project.gitUnknown')
                  : scan.isGit
                    ? t('project.gitYes')
                    : t('project.gitNo')
            }
          />
          <ReadingCell
            label={t('project.currentBranch')}
            tone={read && scan?.branch ? 'info' : 'unknown'}
            value={read && scan?.branch ? scan.branch : t('project.scanUnread')}
            mono
          />
          <ReadingCell
            label={t('project.dirty')}
            tone={read && scan?.dirty != null ? (scan.dirty ? 'warn' : 'ok') : 'unknown'}
            value={
              !read || scan?.dirty == null
                ? t('project.dirtyUnknown')
                : scan.dirty
                  ? t('project.dirtyCount', { count: scan.dirtyFileCount ?? 0 })
                  : t('project.clean')
            }
          />
        </div>

        {/* What the reading could not establish is said out loud, once. */}
        {!scan && (
          <Notice tone="unknown">
            {t('project.scanNever')} {t('project.scanNeverHint')}
          </Notice>
        )}
        {scan?.error && (
          <Notice tone="err">
            {t('project.scanFailed')}
            <details className="k-notice__detail">
              <summary>{t('common.reason')}</summary>
              <span className="k-mono">{scan.error}</span>
            </details>
          </Notice>
        )}
        {read && !scan.pathExists && (
          <Notice tone="err">{t('project.pathMissingHint', { host: hostName })}</Notice>
        )}
        {read && scan.pathExists && !scan.gitAvailable && (
          <Notice tone="warn">{t('project.gitMissingOnHost')}</Notice>
        )}
        {read && scan.gitRoot && (
          <Notice tone="warn">{t('project.gitRootDiffers', { path: scan.gitRoot })}</Notice>
        )}

        <Section label={t('project.location')}>
          <div className="k-projrepo__path">
            <Badge tone="neutral" subtle mono>
              {hostName}
            </Badge>
            <span className="k-mono">{repo.path}</span>
            <CopyButton value={repo.path} ariaLabel={t('common.copy')} />
          </div>
        </Section>

        <Section label={t('project.git')}>
          <KeyValue
            items={[
              {
                key: 'remote',
                label: t('project.remote'),
                value: repo.remote || <Unread />,
                mono: true,
              },
              {
                key: 'branch',
                label: t('project.branch'),
                value: repo.defaultBranch || <Unread />,
                mono: true,
              },
              {
                key: 'head',
                label: t('project.head'),
                value: scan?.headCommit ? (
                  <span className="k-inline">
                    <span className="k-mono">{shortSha(scan.headCommit)}</span>
                    <CopyButton value={scan.headCommit} ariaLabel={t('common.copy')} />
                    {scan.headSubject && (
                      <span className="k-projrepo__subject">{scan.headSubject}</span>
                    )}
                  </span>
                ) : (
                  <Unread />
                ),
              },
              {
                key: 'headat',
                label: t('project.headAuthor'),
                value:
                  scan?.headAuthor || scan?.headAt ? (
                    <span className="k-inline">
                      <span>{scan.headAuthor}</span>
                      {scan.headAt && (
                        <span className="k-mono k-projrepo__when">
                          {formatRelative(scan.headAt, locale)}
                        </span>
                      )}
                    </span>
                  ) : (
                    <Unread />
                  ),
              },
            ]}
          />
        </Section>

        <Section label={t('project.detectedStack')}>
          {repo.detectedStack && repo.detectedStack.length > 0 ? (
            <span className="k-chips">
              {repo.detectedStack.map((s) => (
                <Badge key={s} tone="info" subtle mono>
                  {s}
                </Badge>
              ))}
            </span>
          ) : (
            <Unread />
          )}
        </Section>

        <Section label={t('project.commands')} hint={t('project.commandsUnset')}>
          <KeyValue
            items={[
              {
                key: 'build',
                label: t('project.buildCommand'),
                value: repo.buildCommand || <span className="k-dash">—</span>,
                mono: true,
              },
              {
                key: 'test',
                label: t('project.testCommand'),
                value: repo.testCommand || <span className="k-dash">—</span>,
                mono: true,
              },
              {
                key: 'lint',
                label: t('project.lintCommand'),
                value: repo.lintCommand || <span className="k-dash">—</span>,
                mono: true,
              },
              {
                key: 'worktree',
                label: t('project.worktreeRoot'),
                value: repo.worktreeRoot || <span className="k-dash">—</span>,
                mono: true,
              },
            ]}
          />
        </Section>

        {scan?.worktrees && scan.worktrees.length > 1 && (
          <Section label={t('project.worktrees')}>
            <ul className="k-projrepo__trees">
              {scan.worktrees.map((w) => (
                <li key={w.path}>
                  <span className="k-mono">{w.path}</span>
                  {w.branch && (
                    <Badge tone="info" subtle mono>
                      {w.branch}
                    </Badge>
                  )}
                  {w.isPrimary && (
                    <Badge tone="accent" subtle>
                      {t('project.worktreePrimary')}
                    </Badge>
                  )}
                  {w.detached && (
                    <Badge tone="warn" subtle>
                      {t('project.worktreeDetached')}
                    </Badge>
                  )}
                  {w.locked && (
                    <Badge tone="warn" subtle>
                      {t('project.worktreeLocked')}
                    </Badge>
                  )}
                  {w.prunable && (
                    <Badge tone="warn" subtle>
                      {t('project.worktreePrunable')}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          </Section>
        )}

        <Section label={t('project.configInventoryTitle')} hint={t('project.configNotShownHint')}>
          <ConfigInventory scan={scan} />
        </Section>

        <p className="k-projrepo__foot">
          {scan ? (
            <span className="k-mono">{t('project.scanStale', { when: formatRelative(scan.at, locale) })}</span>
          ) : (
            <span className="k-dash">{t('project.scanNever')}</span>
          )}
        </p>
      </div>
    </Panel>
  )
}

function ReadingCell({
  label,
  value,
  tone,
  mono,
}: {
  label: string
  value: string
  tone: Tone
  mono?: boolean
}) {
  return (
    <div className={`k-rvital k-rvital--${tone}`}>
      <span className="k-label">{label}</span>
      <span className={mono ? 'k-rvital__value k-mono' : 'k-rvital__value'}>{value}</span>
    </div>
  )
}

function Section({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <section className="k-projrepo__section">
      <header className="k-projrepo__sectionhead">
        <span className="k-label">{label}</span>
        {hint && <span className="k-projrepo__hint">{hint}</span>}
      </header>
      {children}
    </section>
  )
}

/** The one word for a fact nobody has looked up. Never an em dash: a dash reads
 *  as "measured and empty", which is a different claim. */
function Unread() {
  const t = useT()
  return (
    <Badge tone="unknown" subtle>
      {t('project.scanUnread')}
    </Badge>
  )
}

/**
 * The configuration inventory: presence and a digest, never contents.
 *
 * Absent entries are rendered too. "There is no AGENTS.md here" is a fact an
 * operator needs, and a list that only showed what exists could not state it.
 */
function ConfigInventory({ scan }: { scan: RepositoryScan | undefined }) {
  const t = useT()

  if (!scan || scan.error || !scan.config || scan.config.length === 0) {
    return <Unread />
  }

  return (
    <div className="k-projconf">
      <ul className="k-projconf__list">
        {scan.config.map((entry) => (
          <li key={entry.path} className="k-projconf__row">
            <StatusDot tone={entry.present ? 'ok' : 'neutral'} label={entry.path} />
            <span className="k-mono">{entry.path}</span>
            <Badge tone={entry.present ? 'ok' : 'neutral'} subtle>
              {entry.present ? t('project.configPresent') : t('project.configMissing')}
            </Badge>
            {entry.digest ? (
              <span className="k-inline">
                <span className="k-mono k-projconf__hash" title={entry.digest}>
                  {entry.digest}
                </span>
                <CopyButton value={entry.digest} ariaLabel={t('common.copy')} />
              </span>
            ) : (
              <span className="k-dash">—</span>
            )}
          </li>
        ))}
      </ul>
      {/* Notice renders a <p>, so the list is its sibling rather than its
          child: a <ul> inside a <p> is closed by the browser before it, and the
          finding would be drawn outside the box that marks it. */}
      {scan.credentialOverrides && scan.credentialOverrides.length > 0 && (
        <>
          <Notice tone="warn">{t('project.credentialOverrideTitle')}</Notice>
          <p className="k-projrepo__hint">{t('project.credentialOverrideBody')}</p>
          <ul className="k-projconf__list">
            {scan.credentialOverrides.map((o) => (
              <li key={o.path + ' ' + o.setting} className="k-projconf__row">
                <StatusDot tone="warn" label={o.path} />
                <span className="k-mono">{o.path}</span>
                <Badge tone="warn" subtle mono>
                  {o.setting}
                </Badge>
              </li>
            ))}
          </ul>
        </>
      )}
      {scan.fingerprint ? (
        <p className="k-projconf__fp">
          <span className="k-label">{t('project.fingerprint')}</span>
          <span className="k-mono k-projconf__hash" title={scan.fingerprint}>
            {scan.fingerprint}
          </span>
          <CopyButton value={scan.fingerprint} ariaLabel={t('common.copy')} />
          <span className="k-projrepo__hint">{t('project.configFingerprintHint')}</span>
        </p>
      ) : (
        <Notice tone="unknown">{t('project.configFingerprintUnknown')}</Notice>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------- Sessions */

function SessionsTab({
  rows,
  pending,
  error,
  onRetry,
}: {
  rows: SessionRow[]
  pending: boolean
  error: unknown
  onRetry: () => void
}) {
  const t = useT()
  const { locale } = useLocale()

  return (
    <Panel title={t('project.sessionsHistory')} flush>
      <QueryState
        pending={pending}
        error={error}
        onRetry={onRetry}
        empty={rows.length === 0}
        emptyTitle={t('project.noSessionsEver')}
        emptyHint={t('project.noSessions')}
      >
        <div className="k-projtable__scroll">
          <table className="k-table">
            <thead>
              <tr>
                <th scope="col">{t('common.state')}</th>
                <th scope="col">{t('account.provider')}</th>
                <th scope="col">{t('topbar.account')}</th>
                <th scope="col">{t('project.path')}</th>
                <th scope="col">{t('common.time')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td>
                    <span className="k-inline">
                      <StatusDot
                        tone={sessionStateTone(s.state)}
                        live={isLive(s.state)}
                        label={s.state}
                      />
                      <Badge tone={sessionStateTone(s.state)} subtle>
                        {s.state}
                      </Badge>
                    </span>
                  </td>
                  <td className="k-mono">{s.provider}</td>
                  <td>
                    {s.accountLabel ?? <span className="k-dash">—</span>}
                    {s.modelName && <span className="k-projrepo__when k-mono">{s.modelName}</span>}
                  </td>
                  <td className="k-mono">{s.workingDir ?? <span className="k-dash">—</span>}</td>
                  <td className="k-mono">
                    {formatDateTime(s.startedAt ?? s.createdAt, locale)}
                  </td>
                  <td>
                    <Link to={`/live/${s.id}`} className="k-proj__back">
                      {t('project.openInCockpit')}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>
    </Panel>
  )
}

/* ------------------------------------------------------------------- Audit */

function AuditTab({ projectId }: { projectId: string }) {
  const t = useT()
  const { locale } = useLocale()
  // The project filter is index-backed server-side (audit_project); the handler
  // simply never parsed it until now.
  const audit = useQuery({
    queryKey: ['project-audit', projectId],
    queryFn: () => api.get<PageResponse<AuditRow>>('/audit', { projectId, limit: 100 }),
  })
  const rows = audit.data?.items ?? []

  return (
    <Panel title={t('project.audit')} flush>
      <QueryState
        pending={audit.isPending}
        error={audit.error}
        onRetry={() => void audit.refetch()}
        empty={rows.length === 0}
        emptyTitle={t('project.noAudit')}
      >
        <div className="k-projtable__scroll">
          <table className="k-table">
            <thead>
              <tr>
                <th scope="col">{t('common.time')}</th>
                <th scope="col">{t('audit.actor')}</th>
                <th scope="col">{t('audit.action')}</th>
                <th scope="col">{t('audit.result')}</th>
                <th scope="col">{t('audit.detail')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="k-mono">{formatDateTime(row.occurredAt, locale)}</td>
                  <td>
                    <span className="k-inline">
                      <Badge tone="neutral" subtle>
                        {row.actorKind}
                      </Badge>
                      <span className="k-mono">{row.actor}</span>
                    </span>
                  </td>
                  <td className="k-mono">{row.action}</td>
                  <td>
                    <Badge tone={resultTone(row.result)} subtle>
                      {row.result}
                    </Badge>
                  </td>
                  <td className="k-mono">{row.detail ?? <span className="k-dash">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>
    </Panel>
  )
}

/* ------------------------------------------------------------ Edit dialog */

const PROJECT_STATES: readonly ProjectState[] = [
  'ONBOARDING',
  'ACTIVE',
  'PAUSED',
  'ARCHIVED',
  'DEGRADED',
]

const RISK_LEVELS = [
  'L0_READ_ONLY',
  'L1_ISOLATED_WRITE',
  'L2_VALIDATED_CHANGE',
  'L3_EXTERNAL_OR_IRREVERSIBLE',
  'L4_FINANCIAL_OR_SECURITY',
] as const

/**
 * Editing a project: the fields PATCH /projects/{id} accepts and nothing else.
 *
 * The version travels with the request, so two operators editing the same
 * project produce a conflict rather than one silently overwriting the other.
 * DEGRADED is offered because a scan sets it and an operator may need to clear
 * it by hand after fixing the host.
 */
function EditProjectDialog({
  project,
  onClose,
}: {
  project: Project
  onClose: () => void
}) {
  const t = useT()
  const queryClient = useQueryClient()
  const apiError = useApiErrorMessage()

  const [name, setName] = useState(project.name)
  const [description, setDescription] = useState(project.description ?? '')
  const [state, setState] = useState<string>(project.state)
  const [risk, setRisk] = useState<string>(project.riskLevel)
  const [timezone, setTimezone] = useState(project.timezone ?? '')

  const save = useMutation({
    mutationFn: () =>
      api.patch<Project>(`/projects/${project.id}`, {
        name: name.trim(),
        description: description.trim(),
        state,
        riskLevel: risk,
        timezone: timezone.trim(),
        version: project.version,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['project', project.id], updated)
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      onClose()
    },
  })

  return (
    <Dialog
      title={t('project.edit')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => save.mutate()}
            disabled={name.trim() === '' || save.isPending}
            busy={save.isPending}
          >
            {t('action.save')}
          </Button>
        </>
      }
    >
      <div className="k-form">
        <TextField label={t('project.name')} value={name} onChange={setName} />
        <TextAreaField
          label={t('project.description')}
          value={description}
          onChange={setDescription}
          placeholder={t('project.descriptionHint')}
          rows={3}
        />
        <SelectField
          label={t('project.state')}
          value={state}
          onChange={setState}
          hint={t('project.stateHint')}
          options={PROJECT_STATES.map((s) => ({ value: s, label: s }))}
        />
        <SelectField
          label={t('project.riskLevel')}
          value={risk}
          onChange={setRisk}
          options={RISK_LEVELS.map((r) => ({ value: r, label: r }))}
        />
        <TextField
          label={t('project.timezone')}
          value={timezone}
          onChange={setTimezone}
          placeholder="Europe/Istanbul"
          mono
        />
        {save.error && <Notice tone="err">{apiError(save.error).title}</Notice>}
      </div>
    </Dialog>
  )
}

/* ---------------------------------------------------------- Delete dialog */

/**
 * Removing a project, pre-flight first.
 *
 * This is not `useConfirm`: a confirm renders one paragraph, and what an
 * operator needs here is a list — what blocks the delete, and what the delete
 * costs. The dialog stays open on a refusal so the blockers can be re-checked
 * after stopping a session in another tab, which a confirm box cannot do.
 *
 * The sentences are composed in the browser from `kind` and `params`. The
 * server's English `title` is the fallback for a kind this build does not know,
 * so a new blocker degrades to readable prose rather than to a missing key.
 */
function DeleteProjectDialog({
  project,
  onClose,
}: {
  project: Project
  onClose: () => void
}) {
  const t = useT()
  const queryClient = useQueryClient()
  const apiError = useApiErrorMessage()

  const preflight = useQuery({
    queryKey: ['project-removal', project.id],
    queryFn: () =>
      api.get<ProjectRemovalCheckResponse>(`/projects/${project.id}/removal-check`),
    // A pre-flight is a snapshot; it must not be served from a cache that
    // predates the session the operator just stopped.
    staleTime: 0,
  })

  const remove = useMutation({
    mutationFn: () => api.del<void>(`/projects/${project.id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.removeQueries({ queryKey: ['project', project.id] })
      onClose()
      // The project is gone, so its own route is gone with it.
      window.location.assign('/projects')
    },
    // The delete re-runs the same checks server-side. A refusal means the world
    // moved since the pre-flight, so the pre-flight is taken again rather than
    // leaving a stale all-clear on screen beside a red banner.
    onError: () => void preflight.refetch(),
  })

  const data = preflight.data
  // Read the two lists through a default. The server always sends them as `[]`,
  // and a contract test pins that — but a dialog that throws on one absent field
  // takes the whole app tree down with it, and this console has already lost
  // three screens that way. A destructive action's dialog is the last place to
  // trust a shape.
  const blockers = data?.blockers ?? []
  const notes = data?.notes ?? []
  const blocked = data != null && (!data.removable || blockers.length > 0)

  return (
    <Dialog
      title={t('project.deleteTitle', { name: project.name })}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          {blocked && (
            <Button
              variant="default"
              onClick={() => void preflight.refetch()}
              busy={preflight.isFetching}
            >
              {t('project.deleteRecheck')}
            </Button>
          )}
          <Button
            variant="danger"
            onClick={() => remove.mutate()}
            disabled={!data || blocked || remove.isPending}
            busy={remove.isPending}
          >
            {t('project.delete')}
          </Button>
        </>
      }
    >
      <div className="k-projdel">
        <p className="k-projdel__meaning">{t('project.deleteMeaning')}</p>

        <QueryState
          pending={preflight.isPending}
          error={preflight.error}
          onRetry={() => void preflight.refetch()}
        >
          {data && (
            <>
              <section className="k-projdel__block">
                <span className="k-label">{t('project.deleteChecks')}</span>
                {blockers.length === 0 ? (
                  <p className="k-inline">
                    <StatusDot tone="ok" label={t('project.deleteReady')} />
                    <span>{t('project.deleteReady')}</span>
                  </p>
                ) : (
                  <>
                    <p className="k-projdel__hint">{t('project.deleteBlockedHint')}</p>
                    <ul className="k-projdel__list">
                      {blockers.map((b, i) => (
                        <li key={`${b.kind}-${b.targetId ?? i}`}>
                          <StatusDot tone="err" label={b.kind} />
                          <span>{<CheckSentence check={b} />}</span>
                          {b.targetType === 'session' && b.targetId && (
                            <Link to={`/live/${b.targetId}`} className="k-proj__back">
                              {t('project.openInCockpit')}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </section>

              {notes.length > 0 && (
                <section className="k-projdel__block">
                  <span className="k-label">{t('project.deleteConsequences')}</span>
                  <ul className="k-projdel__list">
                    {notes.map((n, i) => (
                      <li key={`${n.kind}-${i}`}>
                        <StatusDot tone="info" label={n.kind} />
                        <span>{<CheckSentence check={n} />}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </QueryState>

        {remove.error && <Notice tone="err">{apiError(remove.error).title}</Notice>}
      </div>
    </Dialog>
  )
}

/** One pre-flight line, in the operator's language. */
function CheckSentence({ check }: { check: ProjectRemovalCheck }) {
  const t = useT()
  const p = check.params ?? {}

  switch (check.kind) {
    case 'active_session':
      return (
        <>
          {t('project.deleteBlocker.activeSession', {
            host: p.host ?? '—',
            provider: p.provider ?? '—',
            state: p.state ?? '—',
          })}
        </>
      )
    case 'repositories':
      return (
        <>
          {t('project.deleteNote.repositories', {
            count: p.count ?? '0',
            paths: p.paths ?? '',
          })}
        </>
      )
    case 'session_history':
      return <>{t('project.deleteNote.sessionHistory', { count: p.count ?? '0' })}</>
    case 'project_active':
      return <>{t('project.deleteNote.projectActive')}</>
    default:
      // A kind this build does not know: the server's own sentence beats a
      // missing translation key.
      return <>{check.title}</>
  }
}
